import type { PersonHotline } from '@core/stores/models/hotline-store.model';

const BASE: PersonHotline = {
  holderName: 'dhig Internal Policies',
  hotlinePhone: '',
  insuranceType: 'Health',
  policyEnd: '2026-12-31',
  policyId: 'policy-1',
  policyNo: '101/1241/1500',
  policyPeriodNo: '1',
  policyStart: '2026-01-01',
  progressAssistanceNotification: false,
  status: 'Active'
};

export const HOTLINE_SCENARIOS = {
  /** TC-1.1: Health — 4 phones: US/Canada + Turkey */
  TC_1_1: [
    {
      ...BASE,
      hotlinePhone:
        '+1.800.555.0101 (outside US and Canada) +1.800.555.0102 (US and Canada) ' +
        '+90.444.5550 customer services (The Republic of Turkey) ' +
        '90.216.5550103 authorization center for medical providers (The Republic of Turkey)'
    }
  ],

  /** TC-1.2: Health — 2 phones: Worldwide + US/Canada */
  TC_1_2: [
    {
      ...BASE,
      holderName: 'dhig GmbH',
      hotlinePhone: '+18005550201 (Worldwide)  +1.508.555.0202 (US and Canada)'
    }
  ],

  /** TC-1.3: Health — duplicate phone for different services (Georgia) */
  TC_1_3: [
    {
      ...BASE,
      holderName: 'Optimapharm d.o.o.',
      hotlinePhone:
        '+995 32 2 555 001 customer services (Georgia) ' +
        '+  995 32 2 555 001  authorization center for medical providers (Georgia)'
    }
  ],

  /** TC-1.4: Student — single phone number without context text */
  TC_1_4: [
    {
      ...BASE,
      holderName: 'dhig GmbH',
      insuranceType: 'Student',
      policyNo: '191/1241/1563',
      hotlinePhone: '+18005550400'
    }
  ],

  /** TC-1.5: Travel — 2 phones: Worldwide + Mexico/US/Canada */
  TC_1_5: [
    {
      ...BASE,
      holderName: 'dhig GmbH',
      insuranceType: 'Travel',
      policyNo: '171/1057/1241/1334',
      hotlinePhone: '+1.833.555.0501 (Worldwide)  +1.508.555.0502 (Mexico, US and Canada)'
    }
  ],

  /** TC-1.6: Health — 3 phones: Worldwide + Turkey customer + Turkey providers */
  TC_1_6: [
    {
      ...BASE,
      holderName: 'Optimapharm d.o.o.',
      hotlinePhone:
        '+1.833.555.0601 (Worldwide)  +90.444.5560 customer services (The Republic of Turkey) ' +
        '90.216.5550603 authorization center for medical providers (The Republic of Turkey)'
    }
  ],

  /** TC-1.7: Health — 2 phones: Worldwide + Serbia */
  TC_1_7: [
    {
      ...BASE,
      holderName: 'dhig GmbH',
      hotlinePhone: '+1.800.555.0701 (Worldwide)  +381 11 55 07 020 (Serbia)'
    }
  ],

  /** TC-1.8: Two policies — Student (number-only) + Health (Worldwide + US/Canada) */
  TC_1_8: [
    {
      ...BASE,
      policyId: 'policy-10',
      holderName: 'dhig GmbH',
      insuranceType: 'Student',
      policyNo: '191/1241/1563',
      hotlinePhone: '+18005550800'
    },
    {
      ...BASE,
      policyId: 'policy-11',
      holderName: 'dhig Internal Policies',
      hotlinePhone: '+18005550801 (Worldwide)  +1.508.555.0802 (US and Canada)'
    }
  ],

  /** TC-1.9: Three policies — Student (number-only) + Travel (Worldwide) + Health (4 phones Turkey) */
  TC_1_9: [
    {
      ...BASE,
      policyId: 'policy-20',
      holderName: 'dhig Internal Policies',
      insuranceType: 'Student',
      policyNo: '191/1241/1563',
      hotlinePhone: '+18005550900'
    },
    {
      ...BASE,
      policyId: 'policy-21',
      holderName: 'dhig GmbH',
      insuranceType: 'Travel',
      policyNo: '171/1057/1241/1334',
      hotlinePhone: '+1.833.555.0901 (Worldwide)  +1.508.555.0902 (Mexico, US and Canada)'
    },
    {
      ...BASE,
      policyId: 'policy-22',
      holderName: 'Optimapharm d.o.o.',
      hotlinePhone:
        '+1.800.555.0903 (outside US and Canada) +1.800.555.0904 (US and Canada) ' +
        '+90.444.5590 customer services (The Republic of Turkey) ' +
        '90.216.5550906 authorization center for medical providers (The Republic of Turkey)'
    }
  ],

  /** TC-1.10: Four policies — mixed number-only and multi-number with context */
  TC_1_10: [
    {
      ...BASE,
      policyId: 'policy-30',
      holderName: 'dhig Internal Policies',
      hotlinePhone: '+1.800.555.1001 (Worldwide)  +381 11 55 10 020 (Serbia)'
    },
    {
      ...BASE,
      policyId: 'policy-31',
      holderName: 'dhig GmbH',
      insuranceType: 'Student',
      policyNo: '191/1241/1563',
      hotlinePhone: '+18005551003'
    },
    {
      ...BASE,
      policyId: 'policy-32',
      holderName: 'Optimapharm d.o.o.',
      insuranceType: 'Travel',
      policyNo: '171/1057/1241/1334',
      hotlinePhone: '+1.833.555.1004 (Worldwide)  +1.508.555.1005 (Mexico, US and Canada)'
    },
    {
      ...BASE,
      policyId: 'policy-33',
      holderName: 'dhig GmbH',
      insuranceType: 'Telemedicine',
      policyNo: '181/1241/1688',
      hotlinePhone:
        '+995 32 2 555 006 customer services (Georgia) ' +
        '+  995 32 2 555 006  authorization center for medical providers (Georgia)'
    }
  ],

  /** TC-1.11: Health — mixed separators: dots, hyphens, spaces, no separator */
  TC_1_11: [
    {
      ...BASE,
      holderName: 'Optimapharm d.o.o.',
      hotlinePhone:
        '+1.800.555.1101 (Worldwide) +33-1-55-55-11-02 (France) ' +
        '+41 44 555 11 03 (Switzerland) +971-4-555-1104 (Dubai, UAE) ' +
        '+18005551105 (toll-free US)'
    }
  ],

  /** TC-2.1: Empty array — FeatureUnavailableModalComponent */
  TC_2_1: [] as PersonHotline[]
} satisfies Record<string, PersonHotline[]>;
